import { guards } from '@shared/helpers/typeguards.helpers';
import type { StoreySelector } from '@shared/helpers/storeys.helpers';

/**
 * User-facing storey index convention in **expressions**:
 *
 * | Syntax | First storey | Notes |
 * | --- | --- | --- |
 * | `storeys[n]` | `storeys[1]` | math.js 1-based; first building only |
 * | `getStorey(n)` | `getStorey(1)` | floor `n` of every building |
 * | `getBuildings(i).getStorey(n)` | | floor `n` of the selected building(s) |
 *
 * `storeys_count` is the tallest stack so `getStorey(storeys_count)` stays
 * the last-floor idiom when a sibling building is added.
 *
 * Index `0` is invalid for `getStorey` and throws {@link InvalidGetStoreyIndexError}.
 * Name selectors (`getStorey('Ground floor')`) are unchanged.
 *
 * Not part of the expression language (remain 0-based): TypeScript `floorIndex`,
 * `sumStoreysFromDesignExpressionVariables` selectors, and AI `storeyIndices`.
 *
 * Other expression functions do **not** take storey/array indexes:
 * `getActivity(type)`, `isActivity(activityId)`, `getProduct(idOrName)`,
 * `getParentValue(property, default)`. math.js arrays (`storeys[n]`, `densityArray[n]`)
 * are already 1-based.
 */
export const GET_STOREY_FIRST_INDEX = 1;

export class InvalidGetStoreyIndexError extends Error {
  constructor(index: number) {
    super(
      `getStorey() uses 1-based storey indexes (same convention as storeys[n]). Index ${index} is invalid; use ${GET_STOREY_FIRST_INDEX} for the first storey.`,
    );
    this.name = 'InvalidGetStoreyIndexError';
  }
}

/**
 * Convert a user-facing `getStorey` numeric selector to an internal 0-based floor index.
 * Non-integers and values below {@link GET_STOREY_FIRST_INDEX} throw.
 */
export const toZeroBasedStoreyIndex = (oneBasedIndex: number): number => {
  if (
    !Number.isInteger(oneBasedIndex) ||
    oneBasedIndex < GET_STOREY_FIRST_INDEX
  ) {
    throw new InvalidGetStoreyIndexError(oneBasedIndex);
  }
  return oneBasedIndex - GET_STOREY_FIRST_INDEX;
};

/**
 * Convert `getStorey` selectors from the expression API to internal 0-based indexes.
 * String selectors (storey names / ids) pass through.
 */
export const toInternalGetStoreySelectors = (
  selectors: StoreySelector[],
): Array<number | string> => {
  return selectors.flatMap((selector): Array<number | string> => {
    if (selector === undefined) return [];
    if (!guards.is.number(selector)) return [selector];
    return [toZeroBasedStoreyIndex(selector)];
  });
};

const GET_STOREY_NAME = 'getStorey';

const isIdentifierChar = (char: string | undefined): boolean =>
  Boolean(char && /[A-Za-z0-9_]/.test(char));

const skipQuotedString = (source: string, quoteIndex: number): number => {
  const quote = source[quoteIndex];
  let i = quoteIndex + 1;
  while (i < source.length) {
    if (source[i] === '\\') {
      i += 2;
      continue;
    }
    if (source[i] === quote) return i + 1;
    i += 1;
  }
  return source.length;
};

const findMatchingParen = (source: string, openIndex: number): number => {
  let depth = 0;
  let i = openIndex;
  while (i < source.length) {
    const char = source[i];
    if (char === '"' || char === "'") {
      i = skipQuotedString(source, i);
      continue;
    }
    if (char === '(') depth += 1;
    if (char === ')') {
      depth -= 1;
      if (depth === 0) return i;
    }
    i += 1;
  }
  return -1;
};

const isNumberTokenBoundary = (
  source: string,
  start: number,
  length: number,
): boolean => {
  const before = source[start - 1];
  const after = source[start + length];
  if (before && /[A-Za-z_]/.test(before)) return false;
  if (after === '.' || (after && /[A-Za-z_]/.test(after))) return false;
  return true;
};

type GetStoreyCall = {
  openParen: number;
  closeParen: number;
  argsSource: string;
};

/**
 * Find complete `getStorey(...)` calls. Nested calls are part of the outer
 * argument list, not separate entries (same as the rewrite pass).
 */
const getGetStoreyCalls = (expression: string): GetStoreyCall[] => {
  const calls: GetStoreyCall[] = [];
  let i = 0;
  while (i < expression.length) {
    const char = expression[i];
    if (char === '"' || char === "'") {
      i = skipQuotedString(expression, i);
      continue;
    }
    const isCallStart =
      expression.startsWith(GET_STOREY_NAME, i) &&
      !isIdentifierChar(expression[i - 1]);
    if (isCallStart) {
      const afterName = i + GET_STOREY_NAME.length;
      const openMatch = expression.slice(afterName).match(/^\s*\(/);
      if (openMatch) {
        const openParen = afterName + openMatch[0].length - 1;
        const closeParen = findMatchingParen(expression, openParen);
        if (closeParen === -1) break;
        calls.push({
          openParen,
          closeParen,
          argsSource: expression.slice(openParen + 1, closeParen),
        });
        i = closeParen + 1;
        continue;
      }
    }
    i += 1;
  }
  return calls;
};

const skipWhitespace = (source: string, start: number): number => {
  let i = start;
  while (i < source.length && /\s/.test(source[i] ?? '')) i += 1;
  return i;
};

/**
 * Legacy 0-based `storeys_count - k` selector. 1-based last index is
 * `storeys_count`; second-to-last is `storeys_count-1`.
 * Rejects decimals (`storeys_count-1.5`) and `storeys_count-0`.
 */
const STOREYS_COUNT_OFFSET_IDIOM = /^storeys_count(\s*-\s*)([1-9]\d*)(?!\.\d)/;

/** Already 1-based last storey. Leave unchanged so a retry after rewriting `storeys_count-1` can scan. */
const BARE_STOREYS_COUNT_SELECTOR = /^storeys_count(?!\s*[-+.\w])/;

/**
 * Shift a 0-based `storeys_count - k` selector to 1-based (`k` decreases by 1).
 * {@link STOREYS_COUNT_OFFSET_IDIOM} always captures the minus separator and k.
 */
const rewriteStoreysCountOffsetSelector = (match: RegExpMatchArray): string => {
  const separator = match[1];
  const offsetSource = match[2];
  if (separator === undefined || offsetSource === undefined) {
    throw new Error(
      'STOREYS_COUNT_OFFSET_IDIOM match is missing capture groups',
    );
  }
  const offset = Number(offsetSource);
  if (offset === 1) return 'storeys_count';
  return `storeys_count${separator}${offset - 1}`;
};

/**
 * Rewrite one `getStorey` argument list to 1-based selectors.
 * Returns null when any selector cannot be rewritten without changing meaning.
 *
 * Migratable: non-negative integer literals, quoted names, empty args,
 * `storeys_count` (already 1-based last index, no-op), and `storeys_count-k`
 * for integer k ≥ 1 (optional spaces). Arithmetic, decimals, negatives,
 * nested calls, and other unquoted identifiers stay unmigratable.
 */
const rewriteMigratableGetStoreyArgumentList = (
  argsSource: string,
): string | null => {
  let i = 0;
  let result = '';
  let needsSelector = false;

  while (true) {
    const whitespaceStart = i;
    i = skipWhitespace(argsSource, i);
    result += argsSource.slice(whitespaceStart, i);

    if (i >= argsSource.length) {
      if (needsSelector) return null;
      return result;
    }

    const char = argsSource[i];
    if (char === '"' || char === "'") {
      const end = skipQuotedString(argsSource, i);
      result += argsSource.slice(i, end);
      i = end;
    } else {
      const rest = argsSource.slice(i);
      const storeysCountOffsetMatch = rest.match(STOREYS_COUNT_OFFSET_IDIOM);
      if (storeysCountOffsetMatch) {
        result += rewriteStoreysCountOffsetSelector(storeysCountOffsetMatch);
        i += storeysCountOffsetMatch[0].length;
      } else {
        const bareStoreysCountMatch = rest.match(BARE_STOREYS_COUNT_SELECTOR);
        if (bareStoreysCountMatch) {
          result += bareStoreysCountMatch[0];
          i += bareStoreysCountMatch[0].length;
        } else {
          if (rest.match(/^\d+\.\d+/)) return null;
          const integerMatch = rest.match(/^\d+/);
          if (
            !integerMatch ||
            !isNumberTokenBoundary(argsSource, i, integerMatch[0].length)
          ) {
            return null;
          }
          result += String(Number(integerMatch[0]) + 1);
          i += integerMatch[0].length;
        }
      }
    }

    needsSelector = false;
    const afterSelectorWhitespaceStart = i;
    i = skipWhitespace(argsSource, i);
    result += argsSource.slice(afterSelectorWhitespaceStart, i);

    if (i >= argsSource.length) return result;
    if (argsSource[i] !== ',') return null;
    result += ',';
    i += 1;
    needsSelector = true;
  }
};

/**
 * Argument lists from `getStorey` calls that cannot be rewritten to 1-based
 * indexes (arithmetic, nested calls, decimals, negatives, unquoted identifiers).
 */
export const findNonLiteralGetStoreyArgumentLists = (
  expression: string,
): string[] =>
  getGetStoreyCalls(expression)
    .map((call) => call.argsSource)
    .filter(
      (argsSource) =>
        rewriteMigratableGetStoreyArgumentList(argsSource) === null,
    );

/**
 * Rewrite stored expressions from legacy 0-based `getStorey(n)` to 1-based indexes.
 * `getStorey(0)` becomes `getStorey(1)`; `getStorey(storeys_count-1)` becomes
 * `getStorey(storeys_count)`; `getStorey(storeys_count-2)` becomes
 * `getStorey(storeys_count-1)`; `getStorey(storeys_count)` is left unchanged.
 * Unmigratable calls are left unchanged. `storeys[n]` is not modified.
 */
export const incrementGetStoreyNumericIndexes = (
  expression: string,
): string => {
  const calls = getGetStoreyCalls(expression);
  if (calls.length === 0) return expression;

  let result = '';
  let i = 0;
  for (const call of calls) {
    const rewrittenArgs = rewriteMigratableGetStoreyArgumentList(
      call.argsSource,
    );
    result +=
      expression.slice(i, call.openParen + 1) +
      (rewrittenArgs ?? call.argsSource) +
      ')';
    i = call.closeParen + 1;
  }
  return result + expression.slice(i);
};
